import React, { useEffect, useMemo, useRef, useState } from "react";
import { CatalogService } from "../../../services/catalog.service";
import { appConfirm } from "../../../utils/appPopup";

function normalizeList(value) {
  const raw = value?.data ?? value;
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.items)) return raw.items;
  if (Array.isArray(raw?.data)) return raw.data;
  return [];
}

function normalizeCategory(item = {}) {
  return {
    ...item,
    id: item?.id || "",
    name: item?.name || "",
    slug: item?.slug || "",
    parentId: item?.parentId ?? item?.parent_id ?? null,
    imageUrl: item?.imageUrl ?? item?.image_url ?? "",
    sortOrder: Number(item?.sortOrder ?? item?.sort_order ?? 0),
    childCount: Number(item?.childCount ?? item?.child_count ?? 0),
    directProducts: Number(item?.directProducts ?? item?.direct_products ?? 0),
    isActive: item?.isActive ?? item?.is_active ?? true,
    status: item?.status ?? "active",
  };
}

function toSlug(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function getImageUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const base = String(process.env.REACT_APP_CATALOG_BASE_URL || "").replace(/\/$/, "");
  return base ? `${base}${url.startsWith("/") ? "" : "/"}${url}` : url;
}

function decorateCategories(items = []) {
  const byId = new Map(items.map((item) => [String(item.id), item]));

  const pathFor = (item) => {
    const path = [];
    const seen = new Set();
    let current = item;
    while (current?.id && !seen.has(String(current.id))) {
      seen.add(String(current.id));
      path.unshift(current);
      current = current.parentId ? byId.get(String(current.parentId)) : null;
    }
    return path;
  };

  return items.map((item) => {
    const path = pathFor(item);
    return {
      ...item,
      path,
      depth: Math.max(0, path.length - 1),
      pathLabel: path.map((x) => x.name).filter(Boolean).join(" > "),
    };
  });
}

function CategoryAvatar({ item, size = 48 }) {
  if (item?.imageUrl) {
    return (
      <img
        src={getImageUrl(item.imageUrl)}
        alt={item.name}
        className="shrink-0 rounded-xl border border-rose-100 bg-white object-cover"
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <div
      className="grid shrink-0 place-items-center rounded-xl bg-rose-50 font-black text-[#b75d6a]"
      style={{ width: size, height: size }}
    >
      {(item?.name || "?").slice(0, 1).toUpperCase()}
    </div>
  );
}

function SmallButton({ children, onClick, danger = false, primary = false, disabled = false, title }) {
  const tone = danger
    ? "border-red-200 text-red-600 hover:bg-red-50"
    : primary
      ? "border-[#e5aab3] bg-[#fff7f8] text-[#b85362] hover:bg-[#fff0f2]"
      : "border-slate-200 text-slate-600 hover:bg-slate-50";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`rounded-lg border px-2.5 py-1.5 text-[11px] font-bold transition ${tone} ${disabled ? "cursor-not-allowed opacity-40" : ""}`}
    >
      {children}
    </button>
  );
}

function MainCategoryRow({ item, selected, onSelect, onAdd, onEdit, onDelete }) {
  return (
    <div
      onClick={onSelect}
      className={`cursor-pointer rounded-2xl border p-3 transition ${
        selected
          ? "border-[#d86a78] bg-[#fff7f8] shadow-[0_6px_20px_rgba(216,106,120,0.10)]"
          : "border-slate-200 bg-white hover:border-rose-200 hover:bg-rose-50/30"
      }`}
    >
      <div className="flex items-center gap-3">
        <CategoryAvatar item={item} size={46} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-extrabold text-slate-800" title={item.name}>{item.name}</div>
          <div className="mt-1 text-[10.5px] font-medium text-slate-400">
            {Number(item.childCount || 0)} categories · {Number(item.directProducts || 0)} direct products
          </div>
        </div>
        <div className={`h-2.5 w-2.5 shrink-0 rounded-full ${selected ? "bg-[#d86a78]" : "bg-slate-200"}`} />
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5" onClick={(e) => e.stopPropagation()}>
        <SmallButton primary onClick={onAdd} title="Add Category">
          + Category
        </SmallButton>
        <SmallButton onClick={onEdit}>Edit</SmallButton>
        <SmallButton danger onClick={onDelete}>Delete</SmallButton>
      </div>
    </div>
  );
}

function CategoryCard({ item, selected, onSelect, onAdd, onEdit, onDelete }) {
  return (
    <div
      onClick={onSelect}
      className={`rounded-2xl border p-3.5 transition ${
        selected
          ? "border-[#d86a78] bg-[#fff7f8] shadow-[0_6px_20px_rgba(216,106,120,0.10)]"
          : "border-slate-200 bg-white hover:border-rose-200"
      } ${onSelect ? "cursor-pointer" : ""}`}
    >
      <div className="flex items-start gap-3">
        <CategoryAvatar item={item} size={50} />
        <div className="min-w-0 flex-1">
          <div className="line-clamp-2 text-[13px] font-extrabold leading-5 text-slate-800" title={item.name}>{item.name}</div>
          <div className="mt-1 text-[10.5px] font-medium text-slate-400">
            {Number(item.childCount || 0)} subcategories · {Number(item.directProducts || 0)} direct products
          </div>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5" onClick={(e) => e.stopPropagation()}>
        <SmallButton primary onClick={onAdd} title="Add Subcategory">
          + Subcategory
        </SmallButton>
        <SmallButton onClick={onEdit}>Edit</SmallButton>
        <SmallButton danger onClick={onDelete}>Delete</SmallButton>
      </div>
    </div>
  );
}

function SubcategoryCard({ item, onEdit, onDelete }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3.5 hover:border-rose-200">
      <div className="flex items-start gap-3">
        <CategoryAvatar item={item} size={50} />
        <div className="min-w-0 flex-1">
          <div className="line-clamp-2 text-[13px] font-extrabold leading-5 text-slate-800" title={item.name}>{item.name}</div>
          <div className="mt-1 inline-flex rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-bold text-emerald-700">
            Products go here
          </div>
          <div className="mt-1.5 text-[10.5px] font-medium text-slate-400">
            {Number(item.directProducts || 0)} products
          </div>
        </div>
      </div>
      <div className="mt-3 flex gap-1.5">
        <SmallButton onClick={onEdit}>Edit</SmallButton>
        <SmallButton danger onClick={onDelete}>Delete</SmallButton>
      </div>
    </div>
  );
}

const modeLabels = {
  main: "Main Category",
  category: "Category",
  subcategory: "Subcategory",
};

export default function NestedCategoryManager({ onSaved }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const editorRef = useRef(null);

  const [selectedMainId, setSelectedMainId] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");

  const [editorOpen, setEditorOpen] = useState(false);
  const [mode, setMode] = useState("main");
  const [editingId, setEditingId] = useState("");
  const [name, setName] = useState("");
  const [mainParentId, setMainParentId] = useState("");
  const [categoryParentId, setCategoryParentId] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [existingImage, setExistingImage] = useState("");

  const decorated = useMemo(() => decorateCategories(categories), [categories]);
  const mainCategories = useMemo(
    () => decorated.filter((x) => !x.parentId).sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)),
    [decorated],
  );
  const level2Categories = useMemo(
    () => decorated
      .filter((x) => x.depth === 1 && String(x.parentId || "") === String(selectedMainId || ""))
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)),
    [decorated, selectedMainId],
  );
  const level3Categories = useMemo(
    () => decorated
      .filter((x) => x.depth === 2 && String(x.parentId || "") === String(selectedCategoryId || ""))
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)),
    [decorated, selectedCategoryId],
  );
  const allLevel2 = useMemo(() => decorated.filter((x) => x.depth === 1), [decorated]);
  const formLevel2Options = useMemo(
    () => allLevel2.filter(
      (x) => String(x.parentId || "") === String(mainParentId || "") && String(x.id) !== String(editingId || ""),
    ),
    [allLevel2, mainParentId, editingId],
  );

  const selectedMain = useMemo(
    () => mainCategories.find((x) => String(x.id) === String(selectedMainId)),
    [mainCategories, selectedMainId],
  );
  const selectedLevel2 = useMemo(
    () => level2Categories.find((x) => String(x.id) === String(selectedCategoryId)),
    [level2Categories, selectedCategoryId],
  );

  const load = async () => {
    try {
      setLoading(true);
      const res = await CatalogService.getCategories({ includeInactive: true });
      setCategories(normalizeList(res).map(normalizeCategory));
    } catch (error) {
      setMessage({ type: "err", text: error?.response?.data?.message || error?.message || "Failed to load categories" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => () => {
    if (imagePreview?.startsWith("blob:")) URL.revokeObjectURL(imagePreview);
  }, [imagePreview]);

  useEffect(() => {
    if (!mainCategories.length) {
      setSelectedMainId("");
      return;
    }
    if (!mainCategories.some((x) => String(x.id) === String(selectedMainId))) {
      setSelectedMainId(mainCategories[0].id);
    }
  }, [mainCategories, selectedMainId]);

  useEffect(() => {
    if (!level2Categories.length) {
      setSelectedCategoryId("");
      return;
    }
    if (!level2Categories.some((x) => String(x.id) === String(selectedCategoryId))) {
      setSelectedCategoryId(level2Categories[0].id);
    }
  }, [level2Categories, selectedCategoryId]);

  const clearFormFields = () => {
    setEditingId("");
    setName("");
    setImageFile(null);
    if (imagePreview?.startsWith("blob:")) URL.revokeObjectURL(imagePreview);
    setImagePreview("");
    setExistingImage("");
  };

  const closeEditor = () => {
    clearFormFields();
    setEditorOpen(false);
    setMessage((current) => current.type === "err" ? { type: "", text: "" } : current);
  };

  const revealEditor = () => {
    // The catalog manager has its own scroll container. Wait for React to mount/update
    // the editor, then bring it into view so Add/Edit actions never look unresponsive.
    window.setTimeout(() => {
      editorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 40);
  };

  const startMain = () => {
    clearFormFields();
    setMode("main");
    setMainParentId("");
    setCategoryParentId("");
    setEditorOpen(true);
    setMessage({ type: "", text: "" });
    revealEditor();
  };

  const startCategory = (mainId = selectedMainId) => {
    clearFormFields();
    setMode("category");
    setMainParentId(mainId || "");
    setCategoryParentId("");
    setEditorOpen(true);
    setMessage({ type: "", text: "" });
    revealEditor();
  };

  const startSubcategory = (categoryId = selectedCategoryId) => {
    const category = decorated.find((x) => String(x.id) === String(categoryId));
    clearFormFields();
    setMode("subcategory");
    setMainParentId(category?.parentId || selectedMainId || "");
    setCategoryParentId(category?.id || "");
    setEditorOpen(true);
    setMessage({ type: "", text: "" });
    revealEditor();
  };

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setMessage({ type: "", text: "" });

    if (nextMode === "main") {
      setMainParentId("");
      setCategoryParentId("");
      return;
    }

    const firstAllowedMain = mainCategories.find((item) => String(item.id) !== String(editingId));
    const nextMainId =
      mainParentId && String(mainParentId) !== String(editingId)
        ? mainParentId
        : (String(selectedMainId) !== String(editingId) ? selectedMainId : firstAllowedMain?.id) || "";
    setMainParentId(nextMainId);

    if (nextMode === "category") {
      setCategoryParentId("");
      return;
    }

    const candidates = allLevel2.filter(
      (item) => String(item.parentId || "") === String(nextMainId || "") && String(item.id) !== String(editingId),
    );
    const nextCategoryId =
      categoryParentId && candidates.some((item) => String(item.id) === String(categoryParentId))
        ? categoryParentId
        : candidates[0]?.id || "";
    setCategoryParentId(nextCategoryId);
  };

  const beginEdit = (item) => {
    const path = item.path || [];
    const depth = Number(item.depth || 0);
    clearFormFields();
    setEditingId(item.id);
    setName(item.name || "");
    setExistingImage(item.imageUrl || "");
    setEditorOpen(true);

    if (depth === 0) {
      setMode("main");
      setMainParentId("");
      setCategoryParentId("");
    } else if (depth === 1) {
      setMode("category");
      setMainParentId(item.parentId || path[0]?.id || "");
      setCategoryParentId("");
    } else {
      setMode("subcategory");
      setMainParentId(path[0]?.id || selectedMainId || "");
      const validParent = depth === 2 ? item.parentId : path.find((x, idx) => idx === 1)?.id;
      setCategoryParentId(validParent || "");
    }
    setMessage({ type: "", text: "" });
    revealEditor();
  };

  const onImage = (event) => {
    const file = event.target.files?.[0] || null;
    if (imagePreview?.startsWith("blob:")) URL.revokeObjectURL(imagePreview);
    setImageFile(file);
    setImagePreview(file ? URL.createObjectURL(file) : "");
  };

  const save = async () => {
    const cleanName = name.trim();
    if (!cleanName) return setMessage({ type: "err", text: `${modeLabels[mode]} name is required` });
    if (!editingId && !imageFile) return setMessage({ type: "err", text: `${modeLabels[mode]} image is required` });
    if (mode === "category" && !mainParentId) return setMessage({ type: "err", text: "Please select a Main Category" });
    if (mode === "subcategory" && (!mainParentId || !categoryParentId)) return setMessage({ type: "err", text: "Please select Main Category and Category" });

    const parentId = mode === "main" ? "" : mode === "category" ? mainParentId : categoryParentId;

    try {
      setSaving(true);
      setMessage({ type: "", text: "" });
      const form = new FormData();
      form.append("name", cleanName);
      form.append("slug", toSlug(cleanName));
      form.append("parentId", parentId || "");
      if (imageFile) form.append("image", imageFile);

      if (editingId) await CatalogService.updateCategory(editingId, form);
      else await CatalogService.createCategory(form);

      setMessage({ type: "ok", text: `${modeLabels[mode]} ${editingId ? "updated" : "created"} successfully` });
      clearFormFields();
      setEditorOpen(false);
      await load();
      await onSaved?.();
    } catch (error) {
      setMessage({ type: "err", text: error?.response?.data?.message || error?.message || `Failed to save ${modeLabels[mode]}` });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    const yes = await appConfirm(
      `Delete "${item.name}"? Categories with children or products cannot be deleted.`,
      { danger: true, confirmText: "DELETE" },
    );
    if (!yes) return;

    try {
      await CatalogService.deleteCategory(item.id);
      if (String(editingId) === String(item.id)) closeEditor();
      await load();
      await onSaved?.();
      setMessage({ type: "ok", text: `${item.name} deleted` });
    } catch (error) {
      setMessage({ type: "err", text: error?.response?.data?.message || error?.message || "Failed to delete category" });
    }
  };

  return (
    <div className="space-y-4 pb-2">
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-r from-slate-50 to-rose-50/40 p-4">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <div className="text-[15px] font-black text-slate-800">Catalog structure</div>
            <div className="mt-1 text-[11px] font-medium text-slate-500">
              Keep it simple: products are added only to Subcategories.
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-[11px] font-bold">
            <span className="rounded-xl border border-rose-100 bg-white px-3 py-2 text-slate-700">1. Main Category</span>
            <span className="text-slate-300">→</span>
            <span className="rounded-xl border border-rose-100 bg-white px-3 py-2 text-slate-700">2. Category</span>
            <span className="text-slate-300">→</span>
            <span className="rounded-xl border border-rose-100 bg-white px-3 py-2 text-slate-700">3. Subcategory</span>
            <span className="text-slate-300">→</span>
            <span className="rounded-xl bg-emerald-50 px-3 py-2 text-emerald-700">Products</span>
          </div>
        </div>
      </div>

      {message.text ? (
        <div className={`rounded-xl px-3 py-2.5 text-[12px] font-bold ${message.type === "err" ? "border border-red-200 bg-red-50 text-red-700" : "border border-emerald-200 bg-emerald-50 text-emerald-700"}`}>
          {message.text}
        </div>
      ) : null}

      {editorOpen ? (
        <section ref={editorRef} className="scroll-mt-4 rounded-2xl border border-[#efc5cb] bg-[#fffafb] p-4 shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-[15px] font-black text-slate-800">
                {editingId ? `Edit ${modeLabels[mode]}` : `Add ${modeLabels[mode]}`}
              </div>
              <div className="mt-1 text-[11px] font-medium text-slate-500">
                {editingId
                  ? "You can also change its level/parent without changing the category ID."
                  : mode === "main"
                    ? "Create a department such as Grocery & Kitchen."
                    : mode === "category"
                      ? "Create a Category inside the selected Main Category."
                      : "Create the final Subcategory where products will be added."}
              </div>
            </div>
            <SmallButton onClick={closeEditor}>Close</SmallButton>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            {editingId ? (
              <div>
                <label className="mb-1.5 block text-[11px] font-bold text-slate-600">Category level</label>
                <select
                  value={mode}
                  onChange={(e) => switchMode(e.target.value)}
                  className="h-[42px] w-full rounded-xl border border-rose-200 bg-white px-3 text-[12px] font-semibold text-slate-700 outline-none focus:border-[#d86a78]"
                >
                  <option value="main">Main Category</option>
                  <option value="category">Category</option>
                  <option value="subcategory">Subcategory</option>
                </select>
              </div>
            ) : null}

            {mode !== "main" ? (
              <div>
                <label className="mb-1.5 block text-[11px] font-bold text-slate-600">Main Category *</label>
                <select
                  value={mainParentId}
                  onChange={(e) => {
                    setMainParentId(e.target.value);
                    if (mode === "subcategory") setCategoryParentId("");
                  }}
                  className="h-[42px] w-full rounded-xl border border-rose-200 bg-white px-3 text-[12px] font-semibold text-slate-700 outline-none focus:border-[#d86a78]"
                >
                  <option value="">Select Main Category</option>
                  {mainCategories
                    .filter((item) => String(item.id) !== String(editingId || ""))
                    .map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
              </div>
            ) : null}

            {mode === "subcategory" ? (
              <div>
                <label className="mb-1.5 block text-[11px] font-bold text-slate-600">Category *</label>
                <select
                  value={categoryParentId}
                  onChange={(e) => setCategoryParentId(e.target.value)}
                  className="h-[42px] w-full rounded-xl border border-rose-200 bg-white px-3 text-[12px] font-semibold text-slate-700 outline-none focus:border-[#d86a78]"
                >
                  <option value="">Select Category</option>
                  {formLevel2Options.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
              </div>
            ) : null}

            <div className={editingId ? "" : mode === "main" ? "md:col-span-1 xl:col-span-2" : ""}>
              <label className="mb-1.5 block text-[11px] font-bold text-slate-600">{modeLabels[mode]} Name *</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={mode === "main" ? "e.g. Grocery & Kitchen" : mode === "category" ? "e.g. Atta, Rice & Dal" : "e.g. Atta"}
                className="h-[42px] w-full rounded-xl border border-rose-200 bg-white px-3 text-[12px] font-semibold text-slate-700 outline-none placeholder:font-normal placeholder:text-slate-300 focus:border-[#d86a78]"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-[11px] font-bold text-slate-600">Image {!editingId ? "*" : ""}</label>
              <input
                type="file"
                accept="image/*"
                onChange={onImage}
                className="block h-[42px] w-full rounded-xl border border-rose-200 bg-white px-2 py-2 text-[10px] text-slate-500 file:mr-2 file:rounded-lg file:border-0 file:bg-rose-50 file:px-2 file:py-1 file:text-[10px] file:font-bold file:text-[#b85362]"
              />
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-h-[48px] items-center gap-3">
              {(imagePreview || existingImage) ? (
                <img
                  src={imagePreview || getImageUrl(existingImage)}
                  alt="Category preview"
                  className="h-12 w-12 rounded-xl border border-rose-100 object-cover"
                />
              ) : null}
              <div className="text-[10.5px] font-medium text-slate-400">
                Recommended: square image, clean background.
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={closeEditor}
                className="h-[40px] rounded-xl border border-slate-200 bg-white px-4 text-[11px] font-bold text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="h-[40px] rounded-xl bg-[#d86a78] px-5 text-[11px] font-black text-white shadow-sm hover:bg-[#ca5f6d] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? "Saving..." : editingId ? `Update ${modeLabels[mode]}` : `Create ${modeLabels[mode]}`}
              </button>
            </div>
          </div>
        </section>
      ) : null}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[300px_minmax(0,1fr)]">
        <section className="rounded-2xl border border-slate-200 bg-slate-50/50 p-3.5">
          <div className="mb-3 flex items-center justify-between gap-2">
            <div>
              <div className="text-[13px] font-black text-slate-800">Main Categories</div>
              <div className="mt-0.5 text-[10.5px] font-medium text-slate-400">Choose a department</div>
            </div>
            <button
              type="button"
              onClick={startMain}
              className="rounded-xl bg-[#d86a78] px-3 py-2 text-[10.5px] font-black text-white hover:bg-[#ca5f6d]"
            >
              + Main
            </button>
          </div>

          <div className="max-h-[610px] space-y-2 overflow-y-auto pr-1">
            {loading ? (
              <div className="py-12 text-center text-[11px] font-medium text-slate-400">Loading categories...</div>
            ) : mainCategories.length ? (
              mainCategories.map((item) => (
                <MainCategoryRow
                  key={item.id}
                  item={item}
                  selected={String(item.id) === String(selectedMainId)}
                  onSelect={() => setSelectedMainId(item.id)}
                  onAdd={() => { setSelectedMainId(item.id); startCategory(item.id); }}
                  onEdit={() => beginEdit(item)}
                  onDelete={() => remove(item)}
                />
              ))
            ) : (
              <div className="rounded-xl border border-dashed border-slate-200 bg-white px-4 py-10 text-center text-[11px] font-medium text-slate-400">
                No Main Category yet.
                <div className="mt-2">
                  <button type="button" onClick={startMain} className="font-bold text-[#d86a78]">Create the first one</button>
                </div>
              </div>
            )}
          </div>
        </section>

        <div className="min-w-0 space-y-4">
          <section className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-lg bg-rose-50 text-[11px] font-black text-[#b85362]">2</span>
                  <div className="truncate text-[13px] font-black text-slate-800">
                    Categories {selectedMain ? <span className="font-semibold text-slate-400">inside {selectedMain.name}</span> : null}
                  </div>
                </div>
                <div className="mt-1 pl-8 text-[10.5px] font-medium text-slate-400">Select a Category to manage its Subcategories.</div>
              </div>
              <button
                type="button"
                onClick={() => startCategory()}
                disabled={!selectedMainId}
                className="h-[36px] rounded-xl border border-[#e5aab3] bg-[#fff7f8] px-3 text-[10.5px] font-black text-[#b85362] hover:bg-[#fff0f2] disabled:cursor-not-allowed disabled:opacity-40"
              >
                + Add Category
              </button>
            </div>

            {!selectedMainId ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-[11px] font-medium text-slate-400">Select a Main Category from the left.</div>
            ) : level2Categories.length ? (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 2xl:grid-cols-3">
                {level2Categories.map((item) => (
                  <CategoryCard
                    key={item.id}
                    item={item}
                    selected={String(item.id) === String(selectedCategoryId)}
                    onSelect={() => setSelectedCategoryId(item.id)}
                    onAdd={() => { setSelectedCategoryId(item.id); startSubcategory(item.id); }}
                    onEdit={() => beginEdit(item)}
                    onDelete={() => remove(item)}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-[11px] font-medium text-slate-400">
                No Categories inside {selectedMain?.name || "this Main Category"}.
                <div className="mt-2">
                  <button type="button" onClick={() => startCategory()} className="font-bold text-[#d86a78]">+ Add Category</button>
                </div>
              </div>
            )}
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-lg bg-emerald-50 text-[11px] font-black text-emerald-700">3</span>
                  <div className="truncate text-[13px] font-black text-slate-800">
                    Subcategories {selectedLevel2 ? <span className="font-semibold text-slate-400">inside {selectedLevel2.name}</span> : null}
                  </div>
                </div>
                <div className="mt-1 pl-8 text-[10.5px] font-medium text-slate-400">Products are attached only here.</div>
              </div>
              <button
                type="button"
                onClick={() => startSubcategory()}
                disabled={!selectedCategoryId}
                className="h-[36px] rounded-xl bg-emerald-600 px-3 text-[10.5px] font-black text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                + Add Subcategory
              </button>
            </div>

            {!selectedCategoryId ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-[11px] font-medium text-slate-400">Select a Category above to see its Subcategories.</div>
            ) : level3Categories.length ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {level3Categories.map((item) => (
                  <SubcategoryCard
                    key={item.id}
                    item={item}
                    onEdit={() => beginEdit(item)}
                    onDelete={() => remove(item)}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-[11px] font-medium text-slate-400">
                No Subcategory inside {selectedLevel2?.name || "this Category"}.
                <div className="mt-2">
                  <button type="button" onClick={() => startSubcategory()} className="font-bold text-emerald-700">+ Add Subcategory</button>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>

    </div>
  );
}
